import type { KeyboardEvent, Ref } from 'react'
import { Search, ChevronUp, ChevronDown, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  FIND_BAR_CHIP_CLASS,
  FIND_BAR_ICON_BUTTON_CLASS,
  FIND_BAR_INPUT_CLASS,
} from '@/lib/focus-find-input'

export type FindBarChromeTestIds = {
  bar: string
  input: string
  counter: string
  prev: string
  next: string
  close: string
}

type FindBarChromeProps = {
  inputRef: Ref<HTMLInputElement>
  query: string
  matchLabel: string
  noResults: boolean
  prevDisabled: boolean
  nextDisabled: boolean
  onQueryChange: (value: string) => void
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void
  onPrev: () => void
  onNext: () => void
  onClose: () => void
  prevTitle: string
  nextTitle: string
  testIds: FindBarChromeTestIds
}

/** Shared Cmd+F chip for TipTap edit and markdown preview. Native input (PanelSearchBar), fixed chip size. */
export function FindBarChrome({
  inputRef,
  query,
  matchLabel,
  noResults,
  prevDisabled,
  nextDisabled,
  onQueryChange,
  onKeyDown,
  onPrev,
  onNext,
  onClose,
  prevTitle,
  nextTitle,
  testIds,
}: FindBarChromeProps) {
  return (
    <div className={FIND_BAR_CHIP_CLASS} data-testid={testIds.bar}>
      <Search className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
      <input
        ref={inputRef}
        value={query}
        onChange={(event) => onQueryChange(event.currentTarget.value)}
        onKeyDown={onKeyDown}
        placeholder="Find"
        className={FIND_BAR_INPUT_CLASS}
        data-testid={testIds.input}
      />
      {matchLabel && (
        <span
          className={`text-xs tabular-nums shrink-0 min-w-[3.5rem] px-1 ${
            noResults ? 'text-red-500' : 'text-muted-foreground'
          }`}
          data-testid={testIds.counter}
        >
          {matchLabel}
        </span>
      )}
      <Button
        variant="ghost"
        size="icon"
        className={FIND_BAR_ICON_BUTTON_CLASS}
        onClick={onPrev}
        disabled={prevDisabled}
        title={prevTitle}
        data-testid={testIds.prev}
      >
        <ChevronUp className="w-3.5 h-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className={FIND_BAR_ICON_BUTTON_CLASS}
        onClick={onNext}
        disabled={nextDisabled}
        title={nextTitle}
        data-testid={testIds.next}
      >
        <ChevronDown className="w-3.5 h-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className={FIND_BAR_ICON_BUTTON_CLASS}
        onClick={onClose}
        title="Close (Escape)"
        data-testid={testIds.close}
      >
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
  )
}
